import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, X, Loader2, RefreshCw } from "lucide-react";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = ["Federal", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
}

export default function ComparisonPage() {
  const navigate = useNavigate();
  const [allParties, setAllParties] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeFilter, setActiveFilter] = useState("Federal");
  const [aiComparison, setAiComparison] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [loadingParties, setLoadingParties] = useState(false);

  useEffect(() => {
    fetchParties();
    setAiComparison("");
  }, [activeFilter]);

  const fetchParties = async () => {
    setLoadingParties(true);
    try {
      const type = activeFilter === "Federal" ? "federal" : activeFilter;
      const res = await axios.get(`${API}/parties`, { params: { election_type: type } });
      setAllParties(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingParties(false);
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((s) => s !== id));
      setAiComparison("");
    } else if (selectedIds.length < 4) {
      setSelectedIds([...selectedIds, id]);
      setAiComparison("");
    }
  };

  const selectedParties = allParties.filter((p) => selectedIds.includes(p.id));

  const getAIComparison = async () => {
    if (selectedParties.length < 2) return;
    setLoadingAI(true);
    setAiComparison("");
    try {
      const res = await axios.post(`${API}/ai/party-comparison`, {
        party_ids: selectedParties.map((p) => p.id),
        party_names: selectedParties.map((p) => p.name),
        party_descriptions: selectedParties.map((p) => p.description),
      });
      setAiComparison(res.data.comparison);
    } catch (e) {
      setAiComparison("Failed to generate comparison. Please try again.");
    } finally {
      setLoadingAI(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F7F2" }} data-testid="comparison-page">
      {/* Page Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-6xl mx-auto">
          <button
            data-testid="compare-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Party Comparison
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Compare Parties Side by Side
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Select up to 4 parties to compare their leaders, ideology, and key policies — then get an AI-powered analysis.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b-2 border-black" data-testid="compare-tabs">
          {TABS.map((tab) => {
            const isActive = activeFilter === tab;
            return (
              <button
                key={tab}
                data-testid={`compare-tab-${tab.toLowerCase()}`}
                onClick={() => setActiveFilter(tab)}
                className="px-4 py-2 font-bold text-sm border-2 transition-all"
                style={{
                  borderBottomWidth: isActive ? "0" : "2px",
                  borderColor: isActive ? "#000" : "transparent",
                  backgroundColor: isActive ? "#FFC82E" : "#F3F4F6",
                  color: isActive ? "#000" : "#6B7280",
                  marginBottom: isActive ? "-2px" : "0",
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Selection indicator */}
        <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="selected-parties-indicator">
          <span className="text-sm font-bold text-[#475569]">
            {selectedIds.length === 0
              ? "Select 2–4 parties to compare"
              : `${selectedIds.length} / 4 selected`}
          </span>
          {selectedParties.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-1.5 px-3 py-1 border-2 border-black text-white text-xs font-bold"
              style={{ backgroundColor: p.color }}
            >
              {p.abbreviation}
              <button
                onClick={() => toggleSelect(p.id)}
                data-testid={`deselect-${p.id}`}
                className="hover:opacity-70 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>

        {/* Party Selection Grid */}
        {loadingParties ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 border-2 border-black animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
            {allParties.map((party) => {
              const isSelected = selectedIds.includes(party.id);
              const isDisabled = !isSelected && selectedIds.length >= 4;
              return (
                <button
                  key={party.id}
                  data-testid={`select-party-${party.id}`}
                  onClick={() => !isDisabled && toggleSelect(party.id)}
                  disabled={isDisabled}
                  className="p-4 border-2 border-black text-left transition-all"
                  style={{
                    backgroundColor: isSelected ? party.color : "#fff",
                    opacity: isDisabled ? 0.4 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    boxShadow: isSelected ? "4px 4px 0px 0px #000" : "2px 2px 0px 0px #000",
                    transform: isSelected ? "translateY(-2px)" : "none",
                  }}
                >
                  <div
                    className="text-2xl font-black mb-1"
                    style={{ fontFamily: "Outfit, sans-serif", color: isSelected ? "#fff" : party.color }}
                  >
                    {party.abbreviation}
                  </div>
                  <div
                    className="text-xs font-semibold leading-snug"
                    style={{ color: isSelected ? "rgba(255,255,255,0.9)" : "#475569" }}
                  >
                    {party.name}
                  </div>
                  {isSelected && (
                    <div className="mt-2 text-xs font-bold text-white opacity-80">Selected ✓</div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Comparison Table */}
        {selectedParties.length >= 2 && (
          <div data-testid="comparison-table">
            <h2
              className="text-2xl font-black mb-5"
              style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}
            >
              Side-by-Side Comparison
            </h2>

            <div className="overflow-x-auto mb-6">
              <table
                className="w-full border-2 border-black bg-white"
                style={{ boxShadow: "4px 4px 0px 0px #000", minWidth: "500px" }}
              >
                <thead>
                  <tr>
                    <td className="p-3 bg-gray-100 border-2 border-black font-black text-sm w-36 text-[#475569]">
                      Attribute
                    </td>
                    {selectedParties.map((p) => (
                      <td
                        key={p.id}
                        className="p-4 border-2 border-black text-white font-black text-center"
                        style={{ backgroundColor: p.color }}
                      >
                        <div className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {p.abbreviation}
                        </div>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Full Name", key: "name" },
                    { label: "Leader", key: "leader" },
                    { label: "Ideology", key: "ideology" },
                    { label: "Founded", key: "founded" },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td className="p-3 border-2 border-black font-bold text-sm bg-gray-50 text-[#475569]">
                        {row.label}
                      </td>
                      {selectedParties.map((p) => (
                        <td key={p.id} className="p-3 border-2 border-black text-sm text-[#0F172A]">
                          {p[row.key] || "N/A"}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td className="p-3 border-2 border-black font-bold text-sm bg-gray-50 text-[#475569] align-top">
                      Key Policies
                    </td>
                    {selectedParties.map((p) => (
                      <td key={p.id} className="p-3 border-2 border-black align-top">
                        <ul className="space-y-1.5">
                          {p.positions.map((pos, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-[#0F172A]">
                              <span
                                className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0"
                                style={{ backgroundColor: p.color }}
                              />
                              {pos}
                            </li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-3 border-2 border-black font-bold text-sm bg-gray-50 text-[#475569] align-top">
                      About
                    </td>
                    {selectedParties.map((p) => (
                      <td key={p.id} className="p-3 border-2 border-black text-xs text-[#475569]">
                        {p.description}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* AI Comparison */}
            <div
              className="bg-white border-2 border-black p-6"
              style={{ boxShadow: "4px 4px 0px 0px #000" }}
              data-testid="ai-comparison-section"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3
                    className="text-lg font-black"
                    style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}
                  >
                    AI Political Analysis
                  </h3>
                  <p className="text-xs text-[#475569] mt-1">
                    GPT-4o compares{" "}
                    {selectedParties.map((p) => p.abbreviation).join(" vs ")} across key policy areas
                  </p>
                </div>
                <button
                  data-testid="generate-comparison-btn"
                  onClick={getAIComparison}
                  disabled={loadingAI}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-50"
                  style={{ backgroundColor: "#2E5948", color: "#fff", boxShadow: "2px 2px 0px 0px #000" }}
                >
                  {loadingAI ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> {aiComparison ? "Regenerate" : "Generate"} AI Analysis</>
                  )}
                </button>
              </div>

              {loadingAI && (
                <div className="text-center py-10" data-testid="comparison-loading">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: "#2E5948" }} />
                  <p className="text-sm text-[#475569]">Analysing {selectedParties.length} parties with GPT-4o...</p>
                </div>
              )}

              {!loadingAI && aiComparison && (
                <div
                  data-testid="ai-comparison-content"
                  className="text-sm text-[#0F172A] leading-relaxed"
                  style={{ fontFamily: "Public Sans, sans-serif" }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(`<p>${renderMarkdown(aiComparison)}</p>`),
                  }}
                />
              )}

              {!loadingAI && !aiComparison && (
                <p className="text-sm text-[#475569]">
                  Click "Generate AI Analysis" to get a detailed GPT-4o comparison of how these parties differ on economy, climate, social policy, and more.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
